import { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Send, Save, Eye } from "lucide-react";

const TOKEN_REFERENCE = [
  ["{{name}}", "Recipient's display name"],
  ["{{rank}}", "Their final rank in the closed season"],
  ["{{entrants}}", "Total number of non-bot entrants"],
  ["{{total_points}}", "Their season-total points"],
  ["{{matches_played}}", "Match-days they entered"],
  ["{{champion_name}}", "Name of the season champion"],
  ["{{champion_points}}", "Champion's points"],
  ["{{prize}}", "Configured end-of-league prize (£X.XX)"],
  ["{{season_name}}", "Closed season's name (e.g. Season 1)"],
  ["{{leaderboard_url}}", "Deep link to the final standings"],
];

function tinyMarkdownToHtml(md) {
  const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const paragraphs = md.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return paragraphs
    .map((p) => {
      let h = escape(p).replace(/\n/g, "<br>");
      h = h.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#0038FF;text-decoration:underline;">$1</a>');
      return `<p style="margin:0 0 12px 0;line-height:1.55">${h}</p>`;
    })
    .join("");
}

function renderTokens(text, tokens) {
  let out = text;
  for (const [k, v] of Object.entries(tokens)) {
    out = out.split(`{{${k}}}`).join(String(v));
  }
  return out;
}

const SAMPLE_TOKENS = {
  name: "Sam",
  rank: 7,
  entrants: 42,
  total_points: 116,
  matches_played: 9,
  champion_name: "Carl Reaney",
  champion_points: 154,
  prize: "£500.00",
  season_name: "Season 1",
  leaderboard_url: typeof window !== "undefined" ? `${window.location.origin}/leaderboard?mode=overall` : "https://example.com/leaderboard",
};

export default function BulkEmailCard() {
  const [tpl, setTpl] = useState({ subject: "", body_markdown: "" });
  const [closures, setClosures] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/admin/email-templates/season_end"),
        api.get("/admin/season-closures"),
      ]);
      setTpl({ subject: t.data.subject, body_markdown: t.data.body_markdown });
      setClosures(c.data || []);
      if ((c.data || [])[0]) setSelectedSeasonId(c.data[0].season_id || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/email-templates/season_end", {
        subject: tpl.subject,
        body_markdown: tpl.body_markdown,
      });
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const send = async (mode) => {
    if (mode === "test" && !testEmail.trim()) {
      toast.error("Enter the email address to test-send to");
      return;
    }
    if (mode === "all" && closures.length === 0) {
      toast.error("Close a season first — there's nothing final to send to everyone yet.");
      return;
    }
    if (mode === "all" && !window.confirm("Send this email to EVERY non-bot player who entered the last closed season?")) {
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/email-blasts/season-end", {
        season_id: selectedSeasonId || undefined,
        test_to: mode === "test" ? testEmail.trim() : undefined,
      });
      const previewSuffix = data?.preview_only ? " · preview from the live (active) season" : "";
      const summary = mode === "test"
        ? `Test email sent to ${testEmail.trim()} (${data.sent} sent, ${data.failed} failed)${previewSuffix}`
        : `Blast complete · ${data.sent} sent · ${data.failed} failed · ${data.skipped} skipped`;
      toast.success(summary);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const previewSubject = renderTokens(tpl.subject, SAMPLE_TOKENS);
  const previewBody = renderTokens(tpl.body_markdown, SAMPLE_TOKENS);

  return (
    <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="bulk-email-card">
      <header className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Season-end email blast
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">Tell everyone how the season ended.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Personalised email — each player sees their own rank, points and the champion. Edit the template,
            preview with sample data, then test on yourself before sending to the full roster.
          </p>
        </div>
      </header>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor side */}
          <div className="space-y-4">
            {closures.length >= 1 && (
              <div
                data-testid="blast-just-closed-marker"
                className="rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
              >
                Emailing players about the just-closed season:{" "}
                <strong data-testid="blast-just-closed-name">{closures[0].season_name || "Season"}</strong>
                {closures[0].closed_at && (
                  <> · closed <strong>{new Date(closures[0].closed_at).toLocaleDateString("en-GB")}</strong></>
                )}
                {(closures[0].champion?.name) && (
                  <> · champion <strong>{closures[0].champion.name}</strong></>
                )}
              </div>
            )}
            {closures.length > 1 && (
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Or pick an older closed season</Label>
                <select
                  value={selectedSeasonId}
                  onChange={(e) => setSelectedSeasonId(e.target.value)}
                  data-testid="blast-season-select"
                  className="mt-1 h-10 w-full rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                >
                  {closures.map((c) => (
                    <option key={c.id} value={c.season_id || ""}>
                      {c.season_name || "Season"} · closed {new Date(c.closed_at).toLocaleDateString("en-GB")}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {closures.length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="blast-no-closures">
                No closed seasons yet. <strong>Test send</strong> will preview against the live (active) season so you can verify the layout before closing.
              </p>
            )}

            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Subject</Label>
              <Input
                value={tpl.subject}
                onChange={(e) => setTpl((s) => ({ ...s, subject: e.target.value }))}
                data-testid="blast-subject"
                className="mt-1 h-10 rounded-sm border-zinc-300"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Body (Markdown)</Label>
              <Textarea
                value={tpl.body_markdown}
                onChange={(e) => setTpl((s) => ({ ...s, body_markdown: e.target.value }))}
                data-testid="blast-body"
                rows={14}
                className="mt-1 rounded-sm border-zinc-300 font-mono text-xs"
              />
            </div>

            <div className="text-[11px] text-zinc-500">
              <p className="font-bold uppercase tracking-[0.18em] mb-1">Available tokens</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                {TOKEN_REFERENCE.map(([token, desc]) => (
                  <li key={token} className="truncate">
                    <code className="text-[10px] bg-zinc-100 px-1 py-0.5 rounded">{token}</code> — {desc}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
              <Button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="blast-save-template"
                className="h-10 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold"
              >
                <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save template"}
              </Button>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  data-testid="blast-test-email"
                  className="h-10 w-56 rounded-sm border-zinc-300"
                />
                <Button
                  type="button"
                  onClick={() => send("test")}
                  disabled={sending}
                  variant="outline"
                  data-testid="blast-send-test"
                  className="h-10 rounded-sm"
                >
                  <Eye className="h-4 w-4 mr-1.5" /> Test send
                </Button>
              </div>
              <Button
                type="button"
                onClick={() => send("all")}
                disabled={sending || closures.length === 0}
                data-testid="blast-send-all"
                className="h-10 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold"
              >
                <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending…" : "Send to all"}
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="blast-preview">
            <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Preview · sample tokens</p>
              <p className="text-sm font-bold tracking-tight mt-1 truncate" data-testid="blast-preview-subject">{previewSubject || "(no subject)"}</p>
            </div>
            <div className="p-4">
              <div
                className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-amber-900 bg-amber-200 rounded px-2 py-1 mb-3"
                data-testid="blast-preview-rank-banner"
              >
                You finished #{SAMPLE_TOKENS.rank} of {SAMPLE_TOKENS.entrants}
              </div>
              <div
                className="prose prose-sm max-w-none text-zinc-700"
                data-testid="blast-preview-body"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(tinyMarkdownToHtml(previewBody), {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "a"],
                    ALLOWED_ATTR: ["href", "style", "target", "rel"],
                  }),
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
